import { ResponsiveContainer, ComposedChart, LineChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const tLabel = (t, step) => {
  const d = new Date(t);
  return step >= 60 ? d.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" }) : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

function Card({ title, children, testId }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testId}>
      <div className="text-xs font-semibold mb-2">{title}</div>
      <div className="h-48">{children}</div>
    </div>
  );
}

const Empty = () => <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Collecting data — the first points appear after a minute.</div>;

export function StatusCharts({ series }) {
  const pts = (series?.points || []).map((p) => ({ ...p, label: tLabel(p.t, series.step_minutes) }));
  const tip = { contentStyle: { fontSize: 11 } };
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="system-charts">
      <Card title="Traffic & errors" testId="chart-traffic">
        {pts.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={pts} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar yAxisId="l" dataKey="requests" name="Requests" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="err5" name="5xx errors" stroke="#dc2626" dot={false} strokeWidth={2} />
              <Line yAxisId="r" type="monotone" dataKey="err4" name="4xx" stroke="#f59e0b" dot={false} strokeWidth={1} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>
      <Card title="Response time (ms)" testId="chart-latency">
        {pts.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis yAxisId="api" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="site" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="api" type="monotone" dataKey="p95_ms" name="p95" stroke="#7c3aed" dot={false} strokeWidth={2} />
              <Line yAxisId="api" type="monotone" dataKey="avg_ms" name="average" stroke="#0ea5e9" dot={false} strokeWidth={1.5} />
              <Line yAxisId="site" type="monotone" dataKey="site_ms" name="public site (right axis)" stroke="#10b981" dot={false} strokeWidth={1} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>
      <Card title="CPU & memory (%)" testId="chart-resources">
        {pts.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="cpu_pct" name="CPU" stroke="#ef4444" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="mem_pct" name="Memory" stroke="#3b82f6" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="disk_pct" name="Disk" stroke="#64748b" dot={false} strokeWidth={1} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>
      <Card title="Database ping & event-loop lag (ms)" testId="chart-db">
        {pts.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip {...tip} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="mongo_ping_ms" name="DB ping" stroke="#059669" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="loop_lag_ms" name="loop lag" stroke="#d97706" dot={false} strokeWidth={1.5} />
              <Line type="monotone" dataKey="log_errors" name="logged errors" stroke="#dc2626" dot={false} strokeWidth={1} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>
    </div>
  );
}
